import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery, useQueries } from '@tanstack/react-query';
import {
  getLineData,
  getLineWaitTime,
  getSettings,
} from '~/services/queries/line';
import { getUnit } from '~/services/queries/unit';
import { getBanners, type Banner } from '~/services/queries/banners/useBanners';
import type { LineDataProps } from '~/services/queries/line/useLineData';
import type { UnitProps } from '~/services/queries/unit/useUnit';

type ControllerReturn = {
  loading: boolean;
  error: unknown;
  lineData: LineDataProps | undefined | null;
  unitData: UnitProps | undefined | null;
  banners: Banner[] | undefined | null;
  prediction: number[] | null;
  tolerance: number | null;
  shared: boolean;
  hasFooter: boolean;
};

function parseShortId(raw: string) {
  const idEdited = raw
    ? raw.substring(raw.includes('!') ? 2 : 1, raw.length).replace('-app', ' ')
    : '';
  return {
    idEdited,
    shared: raw.includes('!'),
    hasFooter: !raw.includes('-app'),
  };
}

export function useLineWatchController(): ControllerReturn {
  const router = useRouter();
  const shortIdRaw = useMemo(
    () => (router.query.shortId ? String(router.query.shortId) : ''),
    [router.query.shortId]
  );
  const { idEdited, shared, hasFooter } = useMemo(
    () => parseShortId(shortIdRaw),
    [shortIdRaw]
  );
  const isBootstrapping = !router.isReady || !idEdited;

  const lineQuery = useQuery({
    queryKey: ['line', idEdited],
    enabled: Boolean(idEdited),
    queryFn: async () => {
      const res = await getLineData({ shortId: idEdited });
      if (res.status !== 200) {
        const err: any = new Error('Line not found');
        err.status = res.status;
        throw err;
      }
      return res.data;
    },
    staleTime: 30_000,
  });

  const unitId = lineQuery.data?.unit_id as string | undefined;

  const [unitQuery, settingsQuery, bannersQuery] = useQueries({
    queries: [
      {
        queryKey: ['unit', unitId],
        enabled: Boolean(unitId),
        queryFn: async () => {
          const res = await getUnit({ unit_id: unitId as string });
          if (res.status !== 200)
            throw Object.assign(new Error('Unit error'), {
              status: res.status,
            });
          return res.data;
        },
        staleTime: 60_000,
      },
      {
        queryKey: ['settings', unitId],
        enabled: Boolean(unitId),
        retry: false,
        queryFn: async () => {
          const res = await getSettings({ unit_id: unitId as string });
          if (res.status !== 200)
            throw Object.assign(new Error('Settings error'), {
              status: res.status,
            });
          return res.data;
        },
        staleTime: 60_000,
      },
      {
        queryKey: ['banners', 'line-watch', unitId],
        enabled: Boolean(unitId),
        queryFn: async () => {
          const res = await getBanners({
            type: 'line-watch',
            unit_id: unitId as string,
          });
          if (res.status !== 200)
            throw Object.assign(new Error('Banners error'), {
              status: res.status,
            });
          return res.data;
        },
        staleTime: 60_000,
      },
    ],
  });

  const waitTimeQuery = useQuery({
    queryKey: [
      'waitTime',
      unitQuery.data?.id,
      lineQuery.data?.position,
      lineQuery.data?.people,
    ],
    enabled:
      Boolean(unitQuery.data?.id) &&
      typeof lineQuery.data?.position === 'number' &&
      typeof lineQuery.data?.people === 'number',
    queryFn: async () => {
      const res = await getLineWaitTime({
        line_position: lineQuery.data?.position as number,
        people: lineQuery.data?.people as number,
        unit_id: unitQuery.data?.id as string,
      });
      if (res.status !== 200) return null;
      return res.data?.wait_time_in_minutes ?? null;
    },
    staleTime: 15_000,
  });

  const loading =
    isBootstrapping ||
    lineQuery.isLoading ||
    unitQuery.isLoading ||
    settingsQuery.isLoading ||
    bannersQuery.isLoading ||
    waitTimeQuery.isLoading;

  return {
    loading,
    error: lineQuery.error || unitQuery.error,
    lineData: lineQuery.data ?? null,
    unitData: unitQuery.data ?? null,
    banners: bannersQuery.data ?? null,
    prediction: waitTimeQuery.data ?? null,
    tolerance: settingsQuery.data?.tolerance ?? null,
    shared,
    hasFooter,
  };
}
